/**
 * Utility functions for handling upload errors consistently across the application
 */

export interface UploadErrorInfo {
  type: "validation" | "network" | "server" | "unknown";
  message: string;
  userMessage: string;
  retryable: boolean;
}

/**
 * Parse and categorize upload errors
 */
export const parseUploadError = (error: unknown): UploadErrorInfo => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // File validation errors
    if (
      message.includes("file size") ||
      message.includes("file type") ||
      message.includes("not supported")
    ) {
      return {
        type: "validation",
        message: error.message,
        userMessage: error.message,
        retryable: false,
      };
    }

    // Network errors
    if (
      message.includes("network") ||
      message.includes("failed to fetch") ||
      message.includes("timeout")
    ) {
      return {
        type: "network",
        message: error.message,
        userMessage:
          "Upload failed due to network issues. Please check your connection and try again.",
        retryable: true,
      };
    }

    // Server errors
    if (
      message.includes("server") ||
      message.includes("500") ||
      message.includes("failed to get presigned url")
    ) {
      return {
        type: "server",
        message: error.message,
        userMessage:
          "Upload failed due to server issues. Please try again in a moment.",
        retryable: true,
      };
    }

    // HTTP status specific errors
    if (message.includes("status: 413") || message.includes("413")) {
      return {
        type: "validation",
        message: error.message,
        userMessage:
          "File is too large. Please choose a file under the size limit.",
        retryable: false,
      };
    }
    if (message.includes("status: 415") || message.includes("415")) {
      return {
        type: "validation",
        message: error.message,
        userMessage:
          "Unsupported media type. Please upload a PNG, JPG, WebP, or GIF image.",
        retryable: false,
      };
    }

    // Authentication errors
    if (
      message.includes("authentication") ||
      message.includes("unauthorized") ||
      message.includes("401")
    ) {
      return {
        type: "server",
        message: error.message,
        userMessage:
          "Authentication failed. Please refresh the page and try again.",
        retryable: false,
      };
    }

    // Generic error with message
    return {
      type: "unknown",
      message: error.message,
      userMessage: error.message,
      retryable: true,
    };
  }

  // Unknown error type
  return {
    type: "unknown",
    message: "Unknown error occurred",
    userMessage: "An unexpected error occurred. Please try again.",
    retryable: true,
  };
};

/**
 * Get retry delay based on error type (in milliseconds)
 */
export const getRetryDelay = (
  errorType: UploadErrorInfo["type"],
  attemptNumber: number
): number => {
  const baseDelay = 1000; // 1 second
  const maxDelay = 30000; // 30 seconds

  switch (errorType) {
    case "network":
      // Exponential backoff for network errors
      return Math.min(baseDelay * Math.pow(2, attemptNumber), maxDelay);
    case "server":
      // Linear backoff for server errors
      return Math.min(baseDelay * (attemptNumber + 1), maxDelay);
    case "validation":
      // No retry for validation errors
      return 0;
    default:
      return baseDelay * 2; // 2 seconds for unknown errors
  }
};

/**
 * Check if an error is retryable based on attempt count
 */
export const shouldRetry = (
  errorInfo: UploadErrorInfo,
  attemptNumber: number
): boolean => {
  const maxAttempts = 3;
  return errorInfo.retryable && attemptNumber < maxAttempts;
};
